import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { useCreateFlight, useUpdateBundleOffer, useUpdateFlight } from '~/hooks/apis/accommodation/useBundles';

import { BundleOffer } from '~/services/AccommodationService';

import { getFlightErrorMessage, isAirportCode, normaliseAirportCodes } from '../flightAirports';
import { INCLUSION_ICONS } from '../inclusionIcons';

interface Props {
  bundleId: string;
  // Omitted when adding a new flight.
  offer?: BundleOffer;
  open: boolean;
  onClose: () => void;
}

function AccommodationBundleFlightDialog({ bundleId, offer, open, onClose }: Props) {
  const isEdit = !!offer;
  const initialOrigins = offer?.originAirports ?? [];
  const initialName = offer?.experienceName ?? '';
  const initialDescription = offer?.experienceDescription ?? '';
  const initialIcon = offer?.icon ?? '';

  // Mounted fresh on each open by the parent, so initialisers always see the latest offer.
  const [destinationAirport, setDestinationAirport] = useState(offer?.entityId ?? '');
  const [originAirports, setOriginAirports] = useState<Array<string>>(initialOrigins);
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [icon, setIcon] = useState(initialIcon);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [savedOrigins, setSavedOrigins] = useState(initialOrigins);

  const { enqueueSnackbar } = useSnackbar();

  const createFlightMutation = useCreateFlight();
  const updateFlightMutation = useUpdateFlight();
  const updateBundleOfferMutation = useUpdateBundleOffer();

  const invalidOrigins = originAirports.filter((code) => !isAirportCode(code));
  const destinationError = destinationAirport.length > 0 && !isAirportCode(destinationAirport);
  const originsError = invalidOrigins.length > 0 || originAirports.includes(destinationAirport);
  const originsHelperText =
    invalidOrigins.length > 0
      ? `Not 3-letter IATA codes: ${invalidOrigins.join(', ')}`
      : originAirports.includes(destinationAirport)
        ? 'An origin cannot be the destination.'
        : 'Type a code and press Enter to add it.';
  const canSubmit = isAirportCode(destinationAirport) && originAirports.length > 0 && !originsError;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    if (!offer) {
      try {
        await createFlightMutation.mutateAsync({
          bundleId,
          payload: {
            destinationAirport,
            originAirports,
            name: name.trim() || undefined,
            description: description.trim() || undefined,
            icon: icon.trim() || undefined,
          },
        });
        onClose();
      } catch (err: unknown) {
        setErrorMessage(getFlightErrorMessage(err, destinationAirport));
      }
      return;
    }

    const originsChanged = [...originAirports].sort().join() !== [...savedOrigins].sort().join();
    const contentChanged = name !== initialName || description !== initialDescription || icon !== initialIcon;
    if (originsChanged) {
      try {
        await updateFlightMutation.mutateAsync({ bundleId, offerId: offer.id, payload: { originAirports } });
        setSavedOrigins(originAirports);
      } catch (err: unknown) {
        setErrorMessage(getFlightErrorMessage(err, destinationAirport));
        return;
      }
    }
    if (contentChanged) {
      try {
        // Empty strings are sent so that clearing a field persists; omitted fields are left unchanged.
        await updateBundleOfferMutation.mutateAsync({
          bundleId,
          offerId: offer.id,
          payload: { experienceName: name.trim(), experienceDescription: description.trim(), icon: icon.trim() },
          silent: true,
        });
      } catch {
        // useUpdateBundleOffer already reports the failure in a snackbar.
        return;
      }
    }
    if (originsChanged || contentChanged) {
      enqueueSnackbar('Flight updated', { variant: 'success' });
    }
    onClose();
  };

  const isSubmitting =
    createFlightMutation.isPending || updateFlightMutation.isPending || updateBundleOfferMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEdit ? `Edit Flights to ${destinationAirport}` : 'Add Flights'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Destination Airport"
              value={destinationAirport}
              onChange={(e) => setDestinationAirport(e.target.value.trim().toUpperCase())}
              disabled={isEdit}
              required
              error={destinationError}
              helperText={destinationError ? 'Must be a 3-letter IATA code' : 'IATA code, e.g. DPS'}
              inputProps={{ maxLength: 3 }}
              autoFocus={!isEdit}
            />
            <Autocomplete<string, true, false, true>
              multiple
              freeSolo
              autoSelect
              options={[]}
              value={originAirports}
              onChange={(_event, value) => setOriginAirports(normaliseAirportCodes(value))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Origin Airports"
                  error={originsError}
                  helperText={originsHelperText}
                  required={originAirports.length === 0}
                />
              )}
            />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              helperText="Headline shown above the bundle inclusion. Falls back to the bundle name if empty."
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Body text shown beneath the headline on the offer page."
            />
            <Autocomplete
              freeSolo
              options={INCLUSION_ICONS}
              value={icon}
              onChange={(_event, value) => setIcon(value ?? '')}
              onInputChange={(_event, value) => setIcon(value)}
              renderInput={(params) => <TextField {...params} label="Icon (optional)" />}
              fullWidth
            />
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AccommodationBundleFlightDialog;
